/**
 * Contact page with contact form and company information
 * - Adds page-specific SEO (meta + FAQ JSON-LD) for contact-related queries.
 */

import { useState } from 'react'
import { Button } from '../components/ui/button'
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle, Navigation } from 'lucide-react'
import Seo from '../components/Seo'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  /** Handle form submit with email functionality */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Using Formspree service for email submission (free tier available)
      const response = await fetch('https://formspree.io/f/xdkgqjpb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          service: formData.service,
          message: formData.message,
          _subject: `New Contact Form Submission from ${formData.name}`,
        }),
      })

      if (response.ok) {
        setSubmitStatus('success')
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: ''
        })
      } else {
        throw new Error('Submission failed')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  /** Handle field changes */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  /**
   * FAQ JSON-LD for the Contact page
   */
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I get a proposal from Edicoo?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Use the contact form to share your project details, goals, timeline and budget, and we will provide a tailored proposal after an initial consultation.'
        }
      },
      {
        '@type': 'Question',
        name: 'What are Edicoo\'s working hours?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our senior team provides flexible support. Contact us and we will confirm a suitable meeting or call time for your region.'
        }
      },
      {
        '@type': 'Question',
        name: 'How quickly will Edicoo respond to a contact request?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We aim to acknowledge inquiries promptly and typically respond within a few hours during business time. For urgent requests, phone contact is recommended.'
        }
      }
    ]
  }

  return (
    <div>
      <Seo
        title="Contact — Edicoo"
        description="Contact Edicoo Agency for media planning, digital marketing, influencer campaigns and more. Reach our office in Erbil or send us a message."
        url="https://your-site.com/contact"
        image="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/f2dcbeba-4716-4434-8b8b-01acce34f085.png"
        jsonLd={[faqJsonLd]}
      />

      {/* Hero Section (orange) */}
      <section className="bg-gradient-to-br from-orange-900 via-orange-800 to-orange-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Get in Touch</h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto leading-relaxed">
            Ready to elevate your brand? Let's discuss how our expertise can drive your marketing success. 
            Our senior team is available 24/7 to support your goals.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                
                {/* Submission Status */}
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <p className="text-green-700">Thank you! Your message has been sent successfully. We'll get back to you within 2 hours.</p>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <p className="text-red-700">Sorry, there was an error sending your message. Please try again or contact us directly.</p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="your.email@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Your company"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                        Service Interest
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="">Select a service</option>
                        <option value="digital-marketing">Digital Marketing</option>
                        <option value="media-planning">Media Planning & Buying</option>
                        <option value="influencer-marketing">Influencer Marketing</option>
                        <option value="paid-advertising">Paid Advertising</option>
                        <option value="social-media">Social Media Management</option>
                        <option value="content-creation">Content Creation</option>
                        <option value="translation">Translation & Adaptation</option>
                        <option value="tv-advertising">TV Advertising</option>
                        <option value="ooh-advertising">Out-of-Home Advertising</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Tell us about your project, goals, timeline, and budget..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>Sending... <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div></>
                    ) : (
                      <>Send Message <Send className="ml-2 h-5 w-5" /></>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Details */}
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <MapPin className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Our Office</h4>
                      <p className="text-gray-600 mt-1">
                        Baharke Road, Next to Majesty School<br />
                        Erbil, Iraq
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Phone className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Phone</h4>
                      <p className="text-gray-600 mt-1">+964 750 413 2323</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Mail className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Email</h4>
                      <p className="text-gray-600 mt-1">info@edicoo.com</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Clock className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Availability</h4>
                      <p className="text-gray-600 mt-1">24/7 Senior Team Support</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <Navigation className="h-6 w-6 text-orange-500" />
                  <h3 className="text-xl font-bold text-gray-900">Visit Our Office</h3>
                </div>
                <div className="relative w-full h-64 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d804.1784365262719!2d44.01298194881714!3d36.27072802736247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4007229a711713b7%3A0x5e8c23e40fb448eb!2sEdicoo%20Advertising%2C%20Media%20Planning%2C%20Buying%20and%20Digital%20Marketing%20Agency!5e0!3m2!1sen!2siq!4v1762255503703!5m2!1sen!2siq"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Edicoo Agency Location"
                    className="absolute inset-0"
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <Button 
                    asChild 
                    variant="outline" 
                    size="sm" 
                    className="bg-transparent flex-1"
                  >
                    <a 
                      href="https://maps.google.com/?q=Edicoo+Advertising,+Media+Planning,+Buying+and+Digital+Marketing+Agency,Baharke+Road,Erbil,Iraq" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Open in Maps
                    </a>
                  </Button>
                  <Button 
                    asChild 
                    size="sm" 
                    className="flex-1"
                  >
                    <a 
                      href="https://www.google.com/maps/dir//Edicoo+Advertising,+Media+Planning,+Buying+and+Digital+Marketing+Agency,Baharke+Road,Erbil,Iraq" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Get Directions
                    </a>
                  </Button>
                </div>
              </div>

              {/* Quick Response Promise */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl border border-orange-200">
                <h3 className="text-xl font-bold text-orange-900 mb-4">Quick Response Promise</h3>
                <p className="text-orange-800 mb-4">
                  We pride ourselves on rapid response times and dedicated support. 
                  Our senior team will get back to you within 2 hours during business hours.
                </p>
                <ul className="space-y-2 text-orange-700">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                    Immediate acknowledgment
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                    Strategic consultation
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                    Customized solutions
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section (orange) */}
      <section className="py-20 bg-gradient-to-r from-orange-900 to-orange-800 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-orange-100 mb-8">
            Join the ranks of successful brands who trust Edicoo Agency with their marketing success. 
            Let's create something amazing together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
              <a href="tel:+9647504132323">Call Now</a>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-transparent border-white text-white hover:bg-white hover:text-orange-900">
              <a href="mailto:info@edicoo.com">Send Email</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}